/** The value editor of a `catalogue_product` setting (`tour.join`): another product of the same
 *  catalogue (tenant, restech, account), by its vendor id; clearing it is "none".
 *
 *  Options are the catalogue's rows (the rows endpoint's `search`: name, vendor id, reference),
 *  browsable on open, each with its name, its catalogue pk, the tenant and its vendor id (the
 *  console's "remote autocompletes show tenant + pk" rule). The product being edited is left out.
 *  A product the row already rules out as a target (not synced, not one tour for the product,
 *  removed upstream, or joining a third product itself) is shown disabled with the reason; the
 *  save checks again, with the database.
 *
 *  The stored value is a vendor id only: its name is looked up once (`useCatalogueProductRef`). */
import { TextField } from '@mui/material';
import { useMemo } from 'react';

import * as catalogueApi from '@/api/catalogue';
import type { CatalogueRow, CatalogueScope } from '@/api/catalogue';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';
import type { RecordMeta } from '@/components/RemoteAutocomplete/recordOption';

import { joinTargetBlockedReason } from './catalogueModel';
import { useCatalogueProductRef } from './queries';

/** Where a `catalogue_product` value is picked from. Without it the control is a plain vendor-id
 *  field. */
export interface ProductPickerContext {
  scope: CatalogueScope;
  /** The product being edited: never offered. */
  selfId?: number;
}

/** How many rows one search offers. */
export const PRODUCT_PICKER_PAGE = 25;

/** A product as the picker shows it. `id` is null while the vendor id is not looked up yet. */
export interface ProductRef {
  id: number | null;
  vendor_id: string;
  name: string;
  /** Why it cannot be the product joined, or null. */
  blocked: string | null;
}

export function productRefOf(row: CatalogueRow): ProductRef {
  return {
    id: row.id,
    vendor_id: row.vendor_id,
    name: row.title || row.name,
    blocked: joinTargetBlockedReason(row),
  };
}

export function CatalogueProductControl({
  name,
  value,
  onChange,
  picker,
  disabled = false,
  error,
  label,
}: {
  /** The accessible name. */
  name: string;
  value: unknown;
  onChange: (value: string | null) => void;
  picker?: ProductPickerContext;
  disabled?: boolean;
  error?: string | null;
  label?: string;
}) {
  const vendorId = typeof value === 'string' && value ? value : null;
  const lookup = useCatalogueProductRef(picker?.scope ?? null, vendorId);
  const current = useMemo<ProductRef | null>(() => {
    if (!vendorId) return null;
    if (lookup.data) return productRefOf(lookup.data);
    return { id: null, vendor_id: vendorId, name: '', blocked: null };
  }, [vendorId, lookup.data]);

  if (!picker) {
    return (
      <TextField
        size="small"
        fullWidth
        label={label}
        value={vendorId ?? ''}
        disabled={disabled}
        error={Boolean(error)}
        helperText={error || 'Vendor product id; empty = none'}
        onChange={(e) => onChange(e.target.value.trim() || null)}
        inputProps={{ 'aria-label': name, spellCheck: false }}
      />
    );
  }

  const tenant = picker.scope.tenantCode ?? undefined;
  const meta = (p: ProductRef): RecordMeta => ({
    name: p.name || p.vendor_id,
    // Until the lookup answers, the vendor id is all there is.
    pk: p.id ?? p.vendor_id,
    tenant,
    extra: p.id === null ? [] : [p.vendor_id, p.blocked],
  });

  return (
    <RemoteAutocomplete<ProductRef>
      queryFn={async (q, signal) => {
        const page = await catalogueApi.listRows(
          picker.scope,
          { ...(q ? { search: q } : {}), page: 1, page_size: PRODUCT_PICKER_PAGE },
          signal,
        );
        return page.results.filter((r) => r.id !== picker.selfId).map(productRefOf);
      }}
      getOptionKey={(p) => p.vendor_id}
      getOptionLabel={(p) => p.name || p.vendor_id}
      getOptionMeta={meta}
      getOptionDisabled={(p) => p.blocked !== null}
      value={current}
      onChange={(v) => onChange(v && !Array.isArray(v) ? v.vendor_id : null)}
      fetchOnEmpty
      size="small"
      label={label}
      placeholder="None: it builds its own tours"
      noOptionsText="No product of this catalogue matches."
      errorText="Couldn’t load the catalogue’s products"
      helperText={error || undefined}
      error={Boolean(error)}
      disabled={disabled}
      inputProps={{ 'aria-label': name }}
    />
  );
}
